import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Phone, Send, CheckCircle2, Zap, Lock, Inbox, Sparkles, Loader2, Copy,
  PhoneForwarded, Ban, Search, Flame, Clock, Target, Brain,
  ChevronRight, Mail, MessageSquare, Settings as SettingsIcon, X,
  AlertTriangle, ArrowRight, Users,
} from "lucide-react";
import GatedPage, { usePaywall } from "./GatedPage";
import { conversations as seed } from "../../lib/dummyData";
import { useAuth, apiClient, formatApiError } from "../../lib/auth";
import { toast } from "sonner";
import NumberSetup from "./NumberSetup";
import CallModal from "./CallModal";
import TopUpModal from "./TopUpModal";
import { useSearchParams } from "react-router-dom";

const areaCodeOf = (phone) => { const m = /\+?1?\D*(\d{3})/.exec(phone || ""); return m ? m[1] : ""; };

export default function Messages() {
  return (
    <GatedPage featureName="AI CRM">
      <CRMInner />
    </GatedPage>
  );
}

const STAGES = ["New", "Contacted", "Qualified", "Under Contract", "Closed"];
const STAGE_COLOR = {
  New: "bg-[#1D6AFF]/10 text-[#1552C6] border-[#1D6AFF]/20",
  Contacted: "bg-violet-50 text-violet-700 border-violet-200",
  Qualified: "bg-amber-50 text-amber-700 border-amber-200",
  "Under Contract": "bg-orange-50 text-orange-700 border-orange-200",
  Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const INTENT = {
  high: { label: "High intent", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  medium: { label: "Medium intent", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  low: { label: "Low intent", cls: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};

const lastMsg = (c) => (c.messages && c.messages.length ? c.messages[c.messages.length - 1] : null);
const isUnanswered = (c) => { const m = lastMsg(c); return m && m.from !== "me"; };
const hasRealPhone = (c) => !!(c?.phone && c.phone !== c.name && /\d/.test(String(c.phone)));
const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;

function CRMInner() {
  const { user, updateUser, refresh } = useAuth();
  const { locked, showUpgrade } = usePaywall();

  const [sms, setSms] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showNumberSetup, setShowNumberSetup] = useState(false);
  const [setupDismissed, setSetupDismissed] = useState(false);
  const [callConvo, setCallConvo] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const refreshSms = () => { if (!locked) apiClient.get("/sms/status").then(({ data }) => setSms(data)).catch(() => {}); };
  useEffect(() => { refreshSms(); }, [locked]); // eslint-disable-line
  // Handle return from Stripe top-up checkout.
  useEffect(() => {
    const topup = searchParams.get("topup");
    if (!topup) return;
    if (topup === "success") {
      const sid = searchParams.get("session_id");
      if (sid) {
        apiClient.post("/credits/topup/confirm", { session_id: sid })
          .then(({ data }) => { if (data?.credited) { toast.success("Credits added!", { description: `${(data.credits || 0).toLocaleString()} credits are ready to use.` }); } refreshSms(); })
          .catch(() => {});
      }
    } else if (topup === "canceled") {
      toast.info("Top-up canceled", { description: "No charge was made." });
    }
    searchParams.delete("topup"); searchParams.delete("session_id"); setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line
  // Low-balance alert: warn once when the agent crosses 80% of their monthly limit.
  const lowBalanceWarned = useRef(false);
  useEffect(() => {
    const u = sms?.usage;
    if (!u || locked) return;
    if (u.at_limit) {
      toast.error("You're out of credits this month", { description: "Texting & calling pause until credits refresh on the 1st." });
      lowBalanceWarned.current = true;
    } else if (u.near_limit && !lowBalanceWarned.current) {
      toast.warning("You're running low on credits", { description: `${u.remaining_credits.toLocaleString()} credits left this month.` });
      lowBalanceWarned.current = true;
    } else if (!u.near_limit && !u.at_limit) {
      lowBalanceWarned.current = false;
    }
  }, [sms?.usage?.pct, locked]); // eslint-disable-line
  // First time a paid agent opens the CRM without a number → prompt them to choose one.
  useEffect(() => {
    if (!locked && sms && !sms.active && !setupDismissed) setShowNumberSetup(true);
  }, [locked, sms, setupDismissed]);
  useEffect(() => {
    if (locked || !sms?.active) return;
    const t = setInterval(() => refresh(), 12000);
    return () => clearInterval(t);
  }, [locked, sms?.active, refresh]);

  // Source of truth: user.conversations (paid). Free users get a live-feeling preview.
  const [preview, setPreview] = useState(seed);
  const stored = Array.isArray(user?.conversations) ? user.conversations : [];
  const convos = locked ? preview : stored;

  const persist = async (updated) => {
    if (locked) { setPreview(updated); return; }
    await updateUser({ conversations: updated });
  };
  const patchConvo = async (id, patch) => {
    const updated = convos.map((c) => (c.id === id ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) } : c));
    await persist(updated);
  };

  const [activeId, setActiveId] = useState(convos[0]?.id);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("All");
  const [seen, setSeen] = useState(() => new Set());

  useEffect(() => { if (!convos.find((c) => c.id === activeId)) setActiveId(convos[0]?.id); }, [convos, activeId]);
  const active = convos.find((c) => c.id === activeId);

  // Mark active as seen when opened
  useEffect(() => {
    if (active) setSeen((s) => new Set(s).add(active.id));
  }, [active?.id]); // eslint-disable-line

  const isNew = (c) => isUnanswered(c) && !seen.has(c.id);

  // Smart dashboard metrics
  const metrics = useMemo(() => {
    let newLeads = 0, unanswered = 0, opps = 0;
    convos.forEach((c) => {
      if (isNew(c)) newLeads += 1;
      if (isUnanswered(c)) unanswered += 1;
      if (["Qualified", "Under Contract"].includes(c.stage || "New")) opps += 1;
    });
    return { newLeads, unanswered, opps };
  }, [convos, seen]); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = [...convos];
    if (stageFilter !== "All") list = list.filter((c) => (c.stage || "New") === stageFilter);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((c) => (c.name || "").toLowerCase().includes(q) || (lastMsg(c)?.text || "").toLowerCase().includes(q));
    // unanswered + new first
    return list.sort((a, b) => (isNew(b) ? 1 : 0) - (isNew(a) ? 1 : 0) || (isUnanswered(b) ? 1 : 0) - (isUnanswered(a) ? 1 : 0));
  }, [convos, query, stageFilter, seen]); // eslint-disable-line

  // ── Smart Number ──
  const onNumberActivated = async (data) => {
    setSms((s) => ({ ...(s || {}), active: true, smart_number_display: data.smart_number_display, smart_number: data.smart_number }));
    setShowNumberSetup(false); setSetupDismissed(true);
    await refresh();
    toast.success("Your number is live", { description: `${data.smart_number_display} is ready for calls & texts.` });
  };
  const copyNumber = async () => {
    try { await navigator.clipboard.writeText(sms?.smart_number_display || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e) { /* */ }
  };

  // ── Composer ──
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [emailSubject, setEmailSubject] = useState("");
  const textareaRef = useRef(null);
  useEffect(() => { const el = textareaRef.current; if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 180)}px`; }, [draft]);
  useEffect(() => { setChannel(active?.phone ? "sms" : active?.email ? "email" : "sms"); setDraft(""); }, [active?.id]); // eslint-disable-line

  const send = async () => {
    if (!draft.trim() || !active) return;
    if (locked) { showUpgrade("messaging your leads"); return; }
    const text = draft.trim();
    if (channel === "email") {
      if (!active.email) { setSendError("This lead has no email on file."); return; }
      setDraft(""); setSendError(null);
      try { await apiClient.post("/messages/email", { conversation_id: active.id, to: active.email, subject: emailSubject || "Following up on your home search", body: text }); await refresh(); toast.success("Email sent"); }
      catch (e) { setDraft(text); setSendError(formatApiError(e, "Email didn't send.")); }
      return;
    }
    if (active.opted_out) return;
    setDraft(""); setSendError(null);
    if (sms?.active && active.phone) {
      try { await apiClient.post("/sms/send", { conversation_id: active.id, text }); await refresh(); }
      catch (e) { setDraft(text); setSendError(formatApiError(e, "Message didn't send. Try again.")); }
      return;
    }
    await patchConvo(active.id, (c) => ({ lastTime: "now", messages: [...(c.messages || []), { from: "me", text, time: "Now" }] }));
  };

  // ── AI intelligence ── (free users can try it in viewing mode, limited trials)
  const [analyzing, setAnalyzing] = useState(false);
  const [trialsLeft, setTrialsLeft] = useState(null);
  const runIntel = async () => {
    if (!active) return;
    setAnalyzing(true);
    try {
      const { data } = await apiClient.post("/messages/intel", {
        lead_name: active.name || "", lead_label: active.label || "", source: active.source || active.label || "",
        stage: active.stage || "New",
        messages: (active.messages || []).map((m) => ({ from: m.from, text: m.text })),
      });
      if (typeof data?.trials_remaining === "number") setTrialsLeft(data.trials_remaining);
      await patchConvo(active.id, { intel: { ...data, at: new Date().toISOString() } });
    } catch (e) {
      if (e?.response?.status === 403) { showUpgrade("unlimited AI Lead Intelligence"); }
      else { toast.error("Analysis failed", { description: formatApiError(e) }); }
    }
    finally { setAnalyzing(false); }
  };

  const setStage = (stage) => patchConvo(active.id, { stage });
  const applyFollowup = (t) => { setChannel(active?.phone ? "sms" : "email"); setDraft(t); textareaRef.current?.focus(); };

  return (
    <div data-testid="ai-crm-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1D6AFF] bg-[#1D6AFF]/8 border border-[#1D6AFF]/20 rounded-full px-3 py-1 mb-2">
            <Brain className="w-3 h-3" /> Smart CRM
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Your lead workspace</h1>
          <p className="text-sm text-slate-500 mt-1">Receive, understand, and convert every lead — without leaving Newground.</p>
        </div>
        <button data-testid="crm-setup-toggle" onClick={() => setShowSetup((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 transition-all shadow-sm">
          <SettingsIcon className="w-4 h-4" /> Messaging setup
        </button>
      </div>

      {/* Smart dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="crm-dashboard">
        <Stat icon={Flame} label="New leads" value={metrics.newLeads} tone="rose" />
        <Stat icon={MessageSquare} label="Unanswered" value={metrics.unanswered} tone="blue" />
        <Stat icon={Target} label="Opportunities" value={metrics.opps} tone="emerald" />
      </div>

      {!locked && sms?.usage && (sms.usage.near_limit || sms.usage.at_limit) && (
        <div data-testid="low-balance-alert"
          className={`flex items-start gap-3 rounded-2xl px-4 py-3 border ${sms.usage.at_limit ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-sm flex-1">
            <span className="font-semibold">
              {sms.usage.at_limit ? "You're out of credits this month." : "You're running low on credits."}
            </span>{" "}
            {sms.usage.at_limit
              ? "Texting & calling are paused until you top up or your credits refresh on the 1st."
              : `${sms.usage.remaining_credits.toLocaleString()} of ${sms.usage.cap_credits.toLocaleString()} credits left — texting & calling pause when you hit zero.`}
          </div>
          <button data-testid="low-balance-topup-btn" onClick={() => setShowTopUp(true)}
            className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-1.5 transition-all ${sms.usage.at_limit ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}>
            <Zap className="w-3.5 h-3.5" /> Top up
          </button>
        </div>
      )}

      {/* Setup panel (Smart Number) */}
      {showSetup && (
        <SetupPanel
          locked={locked} sms={sms} onCopy={copyNumber} copied={copied}
          onChooseNumber={() => { setSetupDismissed(false); setShowNumberSetup(true); }}
          onTopUp={() => setShowTopUp(true)}
        />
      )}

      {/* First-run / manual number chooser */}
      <NumberSetup
        open={showNumberSetup}
        onClose={() => { setShowNumberSetup(false); setSetupDismissed(true); }}
        onActivated={onNumberActivated}
        defaultCell={sms?.personal_phone || user?.phone || ""}
        defaultAreaCode={areaCodeOf(sms?.personal_phone || user?.phone)}
      />

      {/* In-app call */}
      <CallModal
        open={!!callConvo}
        convo={callConvo}
        personalPhoneDisplay={sms?.personal_phone || ""}
        onClose={() => setCallConvo(null)}
      />

      {/* Buy more credits */}
      <TopUpModal open={showTopUp} onClose={() => { setShowTopUp(false); refreshSms(); }} />

      {/* Three-panel workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_330px] gap-4" data-testid="crm-workspace">
        {/* Smart Inbox */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-[560px]" data-testid="smart-inbox">
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="flex items-center gap-2 px-1"><Inbox className="w-4 h-4 text-slate-500" /><span className="font-display font-bold text-slate-900">Smart Inbox</span><span className="text-xs text-slate-400 ml-auto">{convos.length}</span></div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="inbox-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search leads…"
                className="w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none rounded-xl pl-8 pr-3 py-2 text-sm transition-all" />
            </div>
            <div className="flex gap-1 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
              {["All", ...STAGES].map((s) => (
                <button key={s} data-testid={`inbox-filter-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setStageFilter(s)}
                  className={`shrink-0 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${stageFilter === s ? "bg-[#1D6AFF] text-white border-[#1D6AFF]" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="inbox-empty">
                <Inbox className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                No leads here yet. New replies from your campaigns land in this inbox automatically.
              </div>
            ) : filtered.map((c) => {
              const lm = lastMsg(c); const neu = isNew(c);
              return (
                <div key={c.id} data-testid={`conversation-${c.id}`} role="button" tabIndex={0}
                  onClick={() => setActiveId(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveId(c.id); } }}
                  className={`w-full text-left px-3.5 py-3 border-b border-slate-50 transition-colors cursor-pointer group ${activeId === c.id ? "bg-[#1D6AFF]/8" : "hover:bg-slate-50"}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1D6AFF] to-[#0B3FA3] text-white text-sm font-semibold flex items-center justify-center shrink-0">{(c.name || "?").charAt(0)}</div>
                      {neu && <span data-testid={`new-dot-${c.id}`} className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-rose-500 border-2 border-white" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate text-slate-900">{c.name}</span>
                        <span className="text-[10px] text-slate-400 shrink-0">{c.lastTime}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{lm ? lm.text : "No messages yet"}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${STAGE_COLOR[c.stage || "New"]}`}>{c.stage || "New"}</span>
                        {(c.source || c.label) && <span className="text-[9px] text-slate-400 truncate">· {c.source || c.label}</span>}
                        {c.intel?.priority && <Flame className="w-3 h-3 text-rose-500" title="Needs attention" />}
                      </div>
                    </div>
                    {hasRealPhone(c) && (
                      !locked && sms?.active ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setCallConvo(c); }}
                          data-testid={`call-lead-${c.id}`} title={`Call ${c.name}`}
                          className="shrink-0 w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center transition-all hover:bg-emerald-500 hover:text-white hover:border-emerald-500 hover:scale-105 opacity-0 group-hover:opacity-100 focus:opacity-100">
                          <Phone className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <a href={telHref(c.phone)} onClick={(e) => e.stopPropagation()}
                          data-testid={`call-lead-${c.id}`} title={`Call ${c.name}`}
                          className="shrink-0 w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center transition-all hover:bg-emerald-500 hover:text-white hover:border-emerald-500 hover:scale-105 opacity-0 group-hover:opacity-100 focus:opacity-100">
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversation */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-[560px]" data-testid="conversation-panel">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-10" data-testid="no-conversation">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-3"><MessageSquare className="w-6 h-6 text-slate-400" /></div>
              <div className="font-display font-bold text-slate-900">Pick a lead to start</div>
              <div className="text-sm text-slate-500 max-w-xs mt-1">Select a conversation from your Smart Inbox to view the thread and AI insights.</div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1D6AFF] to-[#0B3FA3] text-white text-sm font-semibold flex items-center justify-center">{(active.name || "?").charAt(0)}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate text-slate-900">{active.name}</div>
                  {active.opted_out ? (
                    <div className="text-xs text-red-500 flex items-center gap-1" data-testid="opted-out-badge"><Ban className="w-3 h-3" /> Opted out (STOP)</div>
                  ) : (
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      {active.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{active.phone === active.name ? "SMS" : active.phone}</span>}
                      {active.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />Email</span>}
                    </div>
                  )}
                </div>
                {hasRealPhone(active) && (
                  !locked && sms?.active ? (
                    <button type="button" onClick={() => setCallConvo(active)} data-testid="call-active-lead" title={`Call ${active.name}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_8px_18px_-6px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all">
                      <Phone className="w-3.5 h-3.5" /> Call
                    </button>
                  ) : (
                    <a href={telHref(active.phone)} data-testid="call-active-lead" title={`Call ${active.name}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_8px_18px_-6px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all">
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                  )
                )}
                <StageSelect value={active.stage || "New"} onChange={setStage} />
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3 bg-slate-50" data-testid="message-thread">
                {(active.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${m.from === "me" ? "bg-[#1D6AFF] text-white rounded-br-md" : "bg-white text-slate-900 border border-slate-200 rounded-bl-md"}`}>
                      <div>{m.text}</div>
                      <div className={`text-[10px] mt-1 flex items-center gap-1 ${m.from === "me" ? "text-white/80 justify-end" : "text-slate-400"}`}>
                        {m.channel === "email" && <Mail className="w-2.5 h-2.5" />}{m.time}
                      </div>
                    </div>
                  </div>
                ))}
                {(!active.messages || active.messages.length === 0) && <div className="text-center text-xs text-slate-400 py-8">No messages yet — send the first one.</div>}
              </div>

              <div className="border-t border-slate-200 px-4 py-3 bg-white" data-testid="composer">
                {sendError && <div className="text-[11px] text-red-600 mb-2" data-testid="send-error">{sendError}</div>}
                {(active.phone || active.email) && (
                  <div className="flex items-center gap-1.5 mb-2">
                    {active.phone && <ChannelTab active={channel === "sms"} onClick={() => setChannel("sms")} icon={MessageSquare} label="SMS" testId="channel-sms" />}
                    {active.email && <ChannelTab active={channel === "email"} onClick={() => setChannel("email")} icon={Mail} label="Email" testId="channel-email" />}
                  </div>
                )}
                {channel === "email" && (
                  <input data-testid="email-subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject"
                    className="w-full mb-2 bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none rounded-xl px-3.5 py-2 text-sm transition-all" />
                )}
                <div className="flex items-end gap-2">
                  <textarea ref={textareaRef} data-testid="message-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={locked ? "Upgrade to reply to leads…" : active.opted_out && channel === "sms" ? "Lead opted out of SMS" : "Review, edit, then send…"}
                    rows={1} disabled={channel === "sms" && active.opted_out}
                    className="flex-1 bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none rounded-xl px-4 py-2.5 text-sm transition-all resize-none leading-snug min-h-[42px] max-h-[180px] overflow-y-auto disabled:opacity-60" />
                  <button data-testid="message-send" onClick={send} title="Send"
                    className="w-10 h-10 shrink-0 rounded-xl bg-[#1D6AFF] hover:bg-[#1552C6] text-white flex items-center justify-center transition-all">
                    {locked ? <Lock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* AI Lead Intelligence */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-[560px]" data-testid="lead-intel-panel">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-center p-8 text-sm text-slate-400"><Brain className="w-6 h-6 mr-2" /> AI insights appear here</div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-[#1D6AFF]" /><span className="font-display font-bold text-slate-900">Lead Intelligence</span></div>

              {active.intel ? (
                <div className="space-y-3" data-testid="intel-result">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border ${INTENT[active.intel.intent]?.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${INTENT[active.intel.intent]?.dot}`} />{INTENT[active.intel.intent]?.label}</span>
                    {active.intel.priority && <span data-testid="intel-priority" className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border bg-rose-50 text-rose-700 border-rose-200"><Flame className="w-3 h-3" /> Act now</span>}
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Summary</div>
                    <p className="text-sm text-slate-700 leading-relaxed" data-testid="intel-summary">{active.intel.summary}</p>
                  </div>
                  {active.intel.next_action && (
                    <div className="rounded-xl bg-[#1D6AFF]/5 border border-[#1D6AFF]/15 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#1552C6] mb-1 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Next action</div>
                      <p className="text-sm text-slate-800" data-testid="intel-next-action">{active.intel.next_action}</p>
                    </div>
                  )}
                  {active.intel.suggested_followup && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Suggested follow-up</div>
                      <p className="text-sm text-amber-900 leading-relaxed mb-2" data-testid="intel-followup">“{active.intel.suggested_followup}”</p>
                      <button data-testid="use-followup" onClick={() => applyFollowup(active.intel.suggested_followup)} className="text-xs font-bold text-[#1D6AFF] hover:text-[#1552C6] inline-flex items-center gap-1">Use this <ArrowRight className="w-3 h-3" /></button>
                    </div>
                  )}
                  <button data-testid="reanalyze-btn" onClick={runIntel} disabled={analyzing} className="w-full text-xs font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center justify-center gap-1.5 py-1">
                    {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Re-analyze
                  </button>
                </div>
              ) : (
                <button data-testid="analyze-btn" onClick={runIntel} disabled={analyzing}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl text-white font-bold text-sm py-3 bg-gradient-to-r from-[#1D6AFF] to-[#2E7DFF] shadow-[0_14px_30px_-12px_rgba(29,106,255,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-60">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />} {analyzing ? "Analyzing…" : "Analyze this lead"}
                </button>
              )}
              {locked && (
                <p data-testid="intel-trial-note" className="text-[11px] text-center text-slate-400">
                  {trialsLeft === null
                    ? "Try real AI Lead Intelligence free — no credit card needed."
                    : trialsLeft > 0
                      ? `${trialsLeft} free ${trialsLeft === 1 ? "analysis" : "analyses"} left · Upgrade for unlimited.`
                      : "You've used your free previews. Upgrade to Pro for unlimited analysis."}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  const tones = {
    rose: "bg-rose-50 text-rose-600 border-rose-100", blue: "bg-[#1D6AFF]/8 text-[#1552C6] border-[#1D6AFF]/15",
    sky: "bg-sky-50 text-sky-600 border-sky-100", amber: "bg-amber-50 text-amber-600 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3.5 shadow-sm" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center mb-2 ${tones[tone]}`}><Icon className="w-4 h-4" /></div>
      <div className="text-2xl font-bold text-slate-900 font-display leading-none">{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function StageSelect({ value, onChange }) {
  return (
    <select data-testid="stage-select" value={value} onChange={(e) => onChange(e.target.value)}
      className={`text-[11px] font-bold rounded-full pl-2.5 pr-6 py-1 border cursor-pointer outline-none ${STAGE_COLOR[value]}`}>
      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

function ChannelTab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-1.5 text-[11px] font-bold rounded-lg px-2.5 py-1 border transition-colors ${active ? "bg-[#1D6AFF]/10 text-[#1552C6] border-[#1D6AFF]/25" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function SetupPanel({ locked, sms, onCopy, copied, onChooseNumber, onTopUp }) {
  return (
    <div data-testid="crm-setup-panel">
      {/* Smart Number */}
      {locked || sms?.active ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="smart-number-card">
          <div className="flex items-center gap-4 justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center"><Phone className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <div className="font-semibold flex items-center gap-2 text-slate-900 text-sm" data-testid="smart-number-display">Your number: {locked ? "(415) 555-0188" : sms?.smart_number_display}<CheckCircle2 className="w-4 h-4 text-emerald-500" /></div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5"><PhoneForwarded className="w-3.5 h-3.5" />Leads text & call this number; calling works in-app or via your cell.</div>
              </div>
            </div>
            {!locked && <button data-testid="smart-number-copy" onClick={onCopy} className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl px-3.5 py-2 transition-all"><Copy className="w-4 h-4" />{copied ? "Copied!" : "Copy"}</button>}
          </div>
          {!locked && sms?.active && sms?.a2p_status === "pending" && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-sky-50 border border-sky-200 text-sky-800 px-3 py-2.5 text-xs" data-testid="a2p-pending-note">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><span className="font-semibold">Calling is live now.</span> Texting activates shortly (usually within 24 hours) while we finish carrier approval for your number — nothing you need to do.</span>
            </div>
          )}
          {!locked && sms?.usage && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="usage-meter">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-semibold text-slate-600">Calling & texting credits this month</span>
                <span className="tabular-nums text-slate-500">{sms.usage.used_credits.toLocaleString()} / {sms.usage.cap_credits.toLocaleString()} credits{sms.usage.topup_credits > 0 ? ` · +${sms.usage.topup_credits.toLocaleString()} top-up` : ""}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${sms.usage.at_limit ? "bg-red-500" : sms.usage.near_limit ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, sms.usage.pct * 100)}%` }} />
              </div>
              <div className="flex items-center justify-between gap-3 mt-1.5">
                <p className="text-[11px] text-slate-400">
                  {sms.usage.at_limit
                    ? "You're out of credits — top up to keep going."
                    : `${sms.usage.remaining_credits.toLocaleString()} credits left. Refreshes on the 1st.`}
                </p>
                <button data-testid="usage-topup-btn" onClick={onTopUp}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg px-2.5 py-1 transition-all">
                  <Zap className="w-3.5 h-3.5" /> Top up
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 justify-between shadow-sm" data-testid="smart-number-activate-card">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-emerald-600" /></div>
            <div><div className="font-semibold text-slate-900 text-sm">Set up calling & texting</div><div className="text-xs text-slate-500">Pick a local number to call and text leads live.</div></div>
          </div>
          <button data-testid="smart-number-choose" onClick={onChooseNumber}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl px-4 py-2.5 transition-all shadow-sm whitespace-nowrap">
            <Zap className="w-4 h-4" /> Choose your number
          </button>
        </div>
      )}
    </div>
  );
}
